<template>
    <div>
        <div class="top-header">
            <h1>Kommuner</h1>
        </div>
        <div class="page__content page__content--toplist">
            <form @submit.prevent id="toplist-filter">
                <div class="select select--competitions">
                    <select v-model="competition">
                        <option v-for="c in filters.competitions" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                </div>
            </form>
            <div id="toplist-loading" v-if="initialLoading"><div class="toplist__spinner"></div></div>
            <template v-else>
                <div class="toplist-back"><a href="#" class="action" @click.prevent="$emit('back')">&laquo; Tilbake</a></div>
                <div class="table__summary-wrapper">
                    <div class="table__summary">
                        <div class="table__summary-item">Registreringer og aktive brukere basert på hvor de er registrert med postadresse.</div>
                    </div>
                </div>
                <div class="toplist__content" :class="{ 'toplist__content--loading': loading }">
                    <div class="toplist__loading-overlay" v-if="loading"><div class="toplist__spinner"></div></div>
                    <div class="table" v-if="visibleToplist.length">
                        <table>
                            <thead>
                                <tr>
                                    <th class="table__header--ranking">#</th>
                                    <th class="table__header--name">Kommune</th>
                                    <th class="table__header--reg"><a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'visits' }" @click.prevent="order = 'visits'">Registreringer<i v-if="order === 'visits'" class="pi pi-sort-amount-down"></i></a></th>
                                    <th class="table__header--reg"><a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'persons' }" @click.prevent="order = 'persons'">Aktive brukere<i v-if="order === 'persons'" class="pi pi-sort-amount-down"></i></a></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(entry, index) in visibleToplist" :key="entry.county"
                                    class="table__entry"
                                    :class="{ 'table__entry--first': index === 0, 'table__entry--last': index === visibleToplist.length - 1, 'table__entry--other': entry.county_id === null }">
                                    <td class="table__ranking table__num">{{ entry.ranking }}</td>
                                    <td class="table__name">{{ entry.county }}</td>
                                    <td class="table__reg table__num" :class="{ 'table__num--current': order === 'visits' }">{{ regFormat(entry.reg_count) }}</td>
                                    <td class="table__reg table__num" :class="{ 'table__num--current': order === 'persons' }">{{ regFormat(entry.v_count) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { regFormat } from '../config/format.js';

const props = defineProps({ competitionId: { type: Number, default: null } });
defineEmits(['back']);

const params = new URLSearchParams(window.location.search);

const region = ref(params.get('region') || '');
const competition = ref(params.get('competition') ? Number(params.get('competition')) : (props.competitionId || ''));
const order = ref(params.get('order') || 'persons');

const loading = ref(true);
const initialLoading = ref(true);
const toplist = ref([]);
const blockedMunicipalies = ref([]);
const filters = ref({ regions: [], competitions: [] });

// Blocked municipalities are hidden, and their counts folded into the
// synthetic "Andre kommuner" row (users based outside the competition's
// municipalities), which is pinned last and left unranked. The rest are
// dense-ranked by whichever metric is currently sorted - ties share a rank
// (only advancing when the score actually changes), same as the person/
// company/route toplists, rather than every row getting a distinct
// position regardless of ties.
const visibleToplist = computed(() => {
    const isOther = (entry) => entry.county_id === null;
    const isBlocked = (entry) => blockedMunicipalies.value.includes(entry.county_id);
    const filtered = toplist.value.filter((entry) => !isOther(entry) && !isBlocked(entry));
    const metric = order.value === 'visits' ? 'reg_count' : 'v_count';

    let currentRank = 0;
    let previousScore = null;

    const ranked = filtered.map((entry) => {
        const score = entry[metric];
        if (previousScore === null || score !== previousScore) {
            currentRank++;
            previousScore = score;
        }
        return { ...entry, ranking: currentRank };
    });

    const other = toplist.value.find(isOther);
    if (other) {
        const blocked = toplist.value.filter(isBlocked);
        ranked.push({
            ...other,
            ranking: '',
            reg_count: blocked.reduce((sum, entry) => sum + entry.reg_count, other.reg_count),
            v_count: blocked.reduce((sum, entry) => sum + entry.v_count, other.v_count),
        });
    }

    return ranked;
});

let requestToken = 0;

async function load() {
    const token = ++requestToken;
    loading.value = true;

    const search = new URLSearchParams();
    if (region.value) search.set('region', region.value);
    if (competition.value) search.set('competition', competition.value);
    search.set('order', order.value);
    search.set('type', 'counties');
    history.replaceState({}, '', '/topplister?' + search.toString());

    try {
        const response = await fetch('/xhr/frontend/toplists/competition/counties?' + search.toString(), {
            headers: { Accept: 'application/json' },
        });
        if (response.ok && token === requestToken) {
            const data = await response.json();
            toplist.value = data.toplist || [];
            blockedMunicipalies.value = data.blocked_municipalies || [];
            filters.value = { regions: data.regions || [], competitions: data.competitions || [] };
        }
    } catch (e) {
        // Leave the previous state in place.
    } finally {
        if (token === requestToken) {
            loading.value = false;
            initialLoading.value = false;
        }
    }
}

watch([region, competition, order], load);
onMounted(load);
</script>

<style scoped>
/* Separates the unranked "Andre kommuner" row from the ranked municipalities above it. */
.table__entry--other td {
    border-top: 1px solid hsla(0, 0%, 44%, 0.25);
    padding-top: 0.5em;
}
</style>
